import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { authMutationError, useAuth } from '@/hooks/useAuth';
import { validatePassword, validateUsername } from '@/api/auth';
import styles from './AuthForms.module.css';

interface LoginFormProps {
  slug: string;
  onSuccess: () => void;
}

export function LoginForm({ slug, onSuccess }: LoginFormProps) {
  const { loginMutation } = useAuth(slug);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState({ username: false, password: false });

  const usernameError = touched.username ? validateUsername(username) : null;
  const passwordError = touched.password ? validatePassword(password) : null;
  const serverError = loginMutation.error ? authMutationError(loginMutation.error) : null;
  const submitDisabled = loginMutation.isPending
    || validateUsername(username) !== null
    || validatePassword(password) !== null;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched({ username: true, password: true });
    if (submitDisabled) return;
    loginMutation.mutate(
      { username, password },
      { onSuccess },
    );
  }

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      {serverError ? <div className={styles.serverError}>{serverError}</div> : null}
      <FormField
        label="Benutzername"
        type="text"
        autoComplete="username"
        value={username}
        onChange={(e) => setUsername(e.currentTarget.value)}
        onBlur={() => setTouched((t) => ({ ...t, username: true }))}
        error={usernameError}
        required
      />
      <FormField
        label="Passwort"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.currentTarget.value)}
        onBlur={() => setTouched((t) => ({ ...t, password: true }))}
        error={passwordError}
        required
      />
      <Button type="submit" variant="primary" fullWidth disabled={submitDisabled}>
        {loginMutation.isPending ? 'Anmelden…' : 'Einloggen'}
      </Button>
    </form>
  );
}
